import { Avatar, Clickable } from 'components';
import { formatText, normalizeDate, theme } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

/*
  A thread is one top-level comment with its replies beneath it. The rail is the only decoration:
  a quiet line leaves the parent's avatar and bends into each reply, so the nesting reads without
  indenting the text into a narrow column.
*/
const RAIL = `2px solid ${theme.GREY_10}`;
const ROOT_AVATAR = 36;
const REPLY_AVATAR = 24;
const RAIL_X = ROOT_AVATAR / 2 - 1;

const Thread = styled.div`
  position: relative;
  margin-bottom: 14px;
`;

const Item = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  ${(props) =>
    props.$hasReplies &&
    `
    &::after {
      content: '';
      position: absolute;
      left: ${RAIL_X}px;
      top: ${ROOT_AVATAR + 4}px;
      bottom: -6px;
      border-left: ${RAIL};
    }
  `}
`;

const Replies = styled.div`
  margin-left: ${RAIL_X}px;
`;

const ReplyItem = styled(Item)`
  padding: 8px 0 0 ${REPLY_AVATAR - 2}px;
  margin-left: 0;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: ${REPLY_AVATAR - 4}px;
    height: ${REPLY_AVATAR - 2}px;
    border-left: ${RAIL};
    border-bottom: ${RAIL};
    border-bottom-left-radius: 12px;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 0;
    top: ${REPLY_AVATAR - 2}px;
    bottom: 0;
    border-left: ${RAIL};
  }
`;

const Bubble = styled.div`
  flex: 1;
  min-width: 0;
  padding: 8px 12px 9px;
  background: #f6f7f9;
  border-radius: 14px;
  word-break: break-word;
`;

const Meta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 2px;
`;

const NameText = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  color: ${theme.GREY_90};
  cursor: pointer;
`;

const TimeText = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${theme.GREY_50};
`;

const Body = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${theme.GREY_80};
`;

const LinkText = styled.span`
  font-weight: 500;
  color: ${theme.DARK_BLUE};
  cursor: pointer;
`;

const ReadMoreText = styled.span`
  font-weight: 700;
  color: ${theme.GREY_60};
  cursor: pointer;
`;

const Content = ({ comment, isReply, handleClose }) => {
  const [showMore, setShowMore] = useState(false),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    { t } = useTranslation(),
    { body, owner, createdAt, calculatedText = '' } = comment,
    hasReadMore = body !== calculatedText;

  function openProfile() {
    navigate(`/profile/${owner.username}`);
    handleClose();
  }

  function handleGetApp() {
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: {
        showGetApp: true
      }
    });
  }

  function handleTagClick(link, type) {
    if (!showMore) return setShowMore(true);

    switch (type) {
      case 'mention':
        handleClose();
        return navigate(`/profile/${link.slice(1)}`);

      case 'tag':
        handleClose();
        return handleGetApp();
    }
  }

  // Replace the \n's in the text with <br /> tags
  function handleLineBreaks(text) {
    const splitText = text.split('\n');

    return splitText.map((x, i) => (
      <React.Fragment key={i}>
        {x}
        {i !== splitText.length - 1 && <br />}
      </React.Fragment>
    ));
  }

  const Row = isReply ? ReplyItem : Item;

  return (
    <Row $hasReplies={!isReply && !!comment.replies?.length}>
      <Clickable onClick={openProfile}>
        <Avatar user={owner} small={!isReply} tiny={isReply} />
      </Clickable>

      <Bubble>
        <Meta>
          <NameText onClick={openProfile}>{owner.name}</NameText>
          <TimeText>{normalizeDate(createdAt, 'Default', t)}</TimeText>
        </Meta>

        <Body>
          {formatText(showMore ? body : calculatedText).map((x, i) =>
            ['mention', 'tags', 'url'].includes(x.type) ? (
              <LinkText key={i} onClick={() => handleTagClick(x.text, x.type)}>
                {x.text}
              </LinkText>
            ) : (
              <span key={i}>{handleLineBreaks(x.text)}</span>
            )
          )}
          {hasReadMore && (
            <ReadMoreText onClick={() => setShowMore(!showMore)}>
              {` ... ${t(showMore ? 'common.less' : 'common.more')}`}
            </ReadMoreText>
          )}
        </Body>
      </Bubble>
    </Row>
  );
};

const Comment = ({ comment, handleClose }) => {
  if (!comment?.owner) return null;

  const replies = (comment.replies || []).filter((x) => x?.owner);

  return (
    <Thread>
      <Content comment={{ ...comment, replies }} handleClose={handleClose} />

      {!!replies.length && (
        <Replies>
          {replies.map((reply) => (
            <Content key={reply._id} comment={reply} isReply handleClose={handleClose} />
          ))}
        </Replies>
      )}
    </Thread>
  );
};

export default Comment;
